import { supabase } from '../lib/supabase';
import { ClientEvent, EventReminder, EventType, EventStatus } from '../types/client-activity.types';

export const activityService = {
    async getEvents(clientId: string): Promise<ClientEvent[]> {
        const { data, error } = await supabase
            .from('client_events' as any)
            .select(`
        *,
        reminders:event_reminders(*),
        creator:profiles!created_by(id, full_name, email)
      `)
            .eq('client_id', clientId)
            .order('event_date', { ascending: false });

        if (error) throw error;
        return data as ClientEvent[];
    },

    async createEvent(event: Omit<ClientEvent, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'reminders' | 'children' | 'creator'>): Promise<ClientEvent> {
        const { data: userData } = await supabase.auth.getUser();

        // Prepare the payload, excluding optional joins if they are undefined
        const payload = {
            ...event,
            created_by: userData.user?.id
        };

        const { data, error } = await supabase
            .from('client_events' as any)
            .insert([payload])
            .select()
            .single();

        if (error) throw error;
        return data as ClientEvent;
    },

    async updateEvent(id: string, updates: Partial<ClientEvent>): Promise<ClientEvent> {
        const { data, error } = await supabase
            .from('client_events' as any)
            .update(updates)
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        return data as ClientEvent;
    },

    /** Solo gli admin possono eliminare (policy RLS su client_events). */
    async deleteEvent(id: string): Promise<void> {
        // `select()` ci restituisce le righe davvero cancellate: senza permessi
        // la RLS non filtra con un errore, semplicemente non tocca nulla.
        const { data, error } = await supabase
            .from('client_events' as any)
            .delete()
            .eq('id', id)
            .select('id');

        if (error) throw error;
        if (!data || data.length === 0) {
            throw new Error('Attività non eliminata: servono i permessi di amministratore.');
        }
    },

    async createReminder(reminder: Omit<EventReminder, 'id' | 'created_at' | 'created_by'>): Promise<EventReminder> {
        const { data: userData } = await supabase.auth.getUser();

        const payload = {
            ...reminder,
            created_by: userData.user?.id
        }

        const { data, error } = await supabase
            .from('event_reminders' as any)
            .insert([payload])
            .select()
            .single();

        if (error) throw error;
        return data as EventReminder;
    },

    async updateReminder(id: string, updates: Partial<EventReminder>): Promise<EventReminder> {
        const { data, error } = await supabase
            .from('event_reminders' as any)
            .update(updates)
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        return data as EventReminder;
    },

    async deleteReminder(id: string): Promise<void> {
        const { error } = await supabase
            .from('event_reminders' as any)
            .delete()
            .eq('id', id);

        if (error) throw error;
    },

    async getRemindersByClient(clientId: string): Promise<EventReminder[]> {
        const { data, error } = await supabase
            .from('event_reminders' as any)
            .select(`
            *,
            event:client_events!inner(
                client_id
            )
        `)
            .eq('event.client_id', clientId)
            .eq('is_completed', false)
            .order('remind_at', { ascending: true });

        if (error) throw error;
        return data as EventReminder[];
    }
};
